"use client";

import { DEFAULT_PRIMARY_RGB, hexToRgb } from "@/lib/color/hex";
import { useEnterpriseTheme } from "@/lib/enterprise/EnterpriseProvider";
import { Loader2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { FetchTooltip } from "./FetchTooltip";

/** Cached once per process — the device's pointer capabilities don't change at runtime. */
const SUPPORTS_HOVER =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover)").matches;

interface FetchButtonProps {
  children?: React.ReactNode;
  variant?: "primary" | "secondary" | "outline" | "ghost" | "destructive";
  color?: "primary" | "neutral" | "destructive" | "success" | "warning";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  padding?: "xs" | "sm" | "md" | "lg" | "xl";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  iconOnly?: boolean;
  arrow?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
  /**
   * When set, the button renders as an `<a>` (a real link) instead of a `<button>`, keeping every
   * style identical. Use for navigation / "open in new tab" affordances so they match the button
   * set exactly instead of being hand-rolled. Pairs with {@link target} / {@link rel}.
   */
  href?: string;
  /** Anchor target (only used with {@link href}), e.g. "_blank". */
  target?: string;
  /** Anchor rel (only used with {@link href}); defaults to "noopener noreferrer" when target is "_blank". */
  rel?: string;
  tooltip?: string;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  tooltipAlign?: "start" | "center" | "end";
  /** Optional accessible name override (icon-only or ambiguous-label cases). Visible text remains the primary label. */
  "aria-label"?: string;
  /** Optional id of an element describing this button (e.g. a hint paragraph next to a disabled CTA). */
  "aria-describedby"?: string;
  /** Toggle-button selection state. When set, forwarded as `aria-pressed` so screen readers announce selection. */
  ariaPressed?: boolean;
  /**
   * Unavailable, but still reachable. Renders `aria-disabled` and the disabled styling while
   * leaving the button in the tab order, and swallows the click.
   *
   * Use instead of {@link disabled} when the reason is not visible at the point of focus — a real
   * `disabled` button is removed from the tab order entirely, so a keyboard user tabbing through
   * a list finds the primary action simply missing, card after card, with no explanation
   * available. Pair it with an `aria-label` (or `aria-describedby`) that says why.
   *
   * Forces `type="button"` while set. Swallowing `onClick` is not enough on its own: a
   * `type="submit"` button still submits its form on Enter without ever firing a click handler,
   * so an aria-disabled submit would stay fully operable by keyboard — the exact users this
   * prop exists to serve.
   */
  ariaDisabled?: boolean;
}

/**
 * Nowadays themed button.
 */
export const FetchButton = ({
  children,
  variant = "primary",
  color = "primary",
  size = "md",
  padding,
  leftIcon,
  rightIcon,
  iconOnly = false,
  arrow = false,
  onClick,
  className = "",
  disabled = false,
  loading = false,
  type = "button",
  href,
  target,
  rel,
  tooltip,
  tooltipSide = "top",
  tooltipAlign = "center",
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
  ariaPressed,
  ariaDisabled = false,
}: FetchButtonProps) => {
  const enterpriseTheme = useEnterpriseTheme();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (buttonRef.current && !loading) {
      setDimensions({
        width: buttonRef.current.offsetWidth,
        height: buttonRef.current.offsetHeight,
      });
    }
  }, [children, size, iconOnly, leftIcon, rightIcon, arrow, loading]);

  // Size configurations
  const sizeConfig = {
    xs: {
      textClass: "text-tag-sm",
      paddingClass: iconOnly ? "p-1.5" : "px-2.5 py-1.5",
      paddingClassWithRightIcon: iconOnly ? "p-1.5" : "px-2.5 py-1.5 pr-1",
      paddingClassWithLeftIcon: iconOnly ? "p-1.5" : "px-2.5 py-1.5 pl-1",
      iconSize: "w-4 h-4",
    },
    sm: {
      textClass: "text-tag-sm",
      paddingClass: iconOnly ? "p-2" : "px-3 py-2",
      paddingClassWithRightIcon: iconOnly ? "p-2" : "px-3 py-2 pr-1.5",
      paddingClassWithLeftIcon: iconOnly ? "p-2" : "px-3 py-2 pl-1.5",
      iconSize: "w-5 h-5",
    },
    md: {
      textClass: "text-tag-md",
      paddingClass: iconOnly ? "p-3" : "px-4 py-3",
      paddingClassWithRightIcon: iconOnly ? "p-3" : "px-4 py-3 pr-2",
      paddingClassWithLeftIcon: iconOnly ? "p-3" : "px-4 py-3 pl-2",
      iconSize: "w-5 h-5",
    },
    lg: {
      textClass: "text-tag-md",
      paddingClass: iconOnly ? "p-3" : "px-5 py-3.5",
      paddingClassWithRightIcon: iconOnly ? "p-3" : "px-5 py-3.5 pr-2.5",
      paddingClassWithLeftIcon: iconOnly ? "p-3" : "px-5 py-3.5 pl-2.5",
      iconSize: "w-5 h-5",
    },
    xl: {
      textClass: "text-tag-lg",
      paddingClass: iconOnly ? "p-4" : "px-6 py-4",
      paddingClassWithRightIcon: iconOnly ? "p-4" : "px-6 py-4 pr-3",
      paddingClassWithLeftIcon: iconOnly ? "p-4" : "px-6 py-4 pl-3",
      iconSize: "w-7 h-7",
    },
  };

  const currentSize = sizeConfig[size];
  const currentPadding = padding ? sizeConfig[padding] : currentSize;

  // Determine padding class based on icons
  const getPaddingClass = () => {
    if (iconOnly) return currentPadding.paddingClass;
    if (leftIcon && !rightIcon && !arrow) return currentPadding.paddingClassWithLeftIcon;
    if ((rightIcon || arrow) && !leftIcon) return currentPadding.paddingClassWithRightIcon;
    return currentPadding.paddingClass;
  };

  // `focus:ring-0` clears the default (always-on) ring so it doesn't show on a mouse click;
  // `focus-visible:ring-2` puts a visible ring back for keyboard focus specifically.
  const baseClasses = `landing-text ${currentSize.textClass} ${getPaddingClass()} rounded-xl inline-flex touch-manipulation justify-center items-center gap-2 cursor-pointer transition-all duration-300 border font-medium focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed active:scale-95 select-none relative overflow-hidden`;

  // Color theme configurations (static colors, no interpolation)
  const getColorStyles = (colorTheme: string) => {
    switch (colorTheme) {
      // "primary" and the fallback both resolve to the current enterprise theme's colors,
      // so they share one body instead of two copies of the same object shape.
      case "primary":
      default: {
        const { primaryColor, primaryHoverColor, primarySecondaryBg, primarySecondaryHoverBg, indigoColors } =
          enterpriseTheme;
        const primaryRgb = hexToRgb(primaryColor) ?? DEFAULT_PRIMARY_RGB;
        const secondaryTextColor = indigoColors ? indigoColors[900] : "#27253b";

        return {
          solid: {
            bg: primaryColor,
            text: "#ffffff",
            border: primaryColor,
            hoverBg: primaryHoverColor,
            hoverText: "#ffffff",
          },
          secondary: {
            bg: primarySecondaryBg,
            text: secondaryTextColor,
            border: "transparent",
            hoverBg: primarySecondaryHoverBg,
            hoverText: secondaryTextColor,
          },
          outline: {
            bg: `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.05)`,
            text: primaryColor,
            border: `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.3)`,
            hoverBg: primaryColor,
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: primaryColor,
            border: "transparent",
            hoverBg: `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`,
            hoverText: primaryColor,
          },
        };
      }

      case "neutral":
        return {
          solid: {
            bg: "#2D3748",
            text: "#ffffff",
            border: "#2D3748",
            hoverBg: "#1A202C",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#F7FAFC",
            text: "#4A5568",
            border: "transparent",
            hoverBg: "#EDF2F7",
            hoverText: "#4A5568",
          },
          outline: {
            bg: "rgba(74, 85, 104, 0.05)",
            text: "#4A5568",
            border: "rgba(203, 213, 224, 0.6)",
            hoverBg: "#F7FAFC",
            hoverText: "#4A5568",
          },
          ghost: {
            bg: "transparent",
            text: "#4A5568",
            border: "transparent",
            hoverBg: "rgba(74, 85, 104, 0.1)",
            hoverText: "#4A5568",
          },
        };

      case "destructive":
        return {
          solid: {
            bg: "#ef4444",
            text: "#ffffff",
            border: "#ef4444",
            hoverBg: "#dc2626",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#fee2e2",
            text: "#dc2626",
            border: "transparent",
            hoverBg: "#fecaca",
            hoverText: "#dc2626",
          },
          outline: {
            bg: "rgba(239, 68, 68, 0.05)",
            text: "#ef4444",
            border: "rgba(239, 68, 68, 0.3)",
            hoverBg: "#ef4444",
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: "#ef4444",
            border: "transparent",
            hoverBg: "rgba(239, 68, 68, 0.1)",
            hoverText: "#ef4444",
          },
        };

      case "success":
        return {
          solid: {
            bg: "#10b981",
            text: "#ffffff",
            border: "#10b981",
            hoverBg: "#059669",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#d1fae5",
            text: "#065f46",
            border: "transparent",
            hoverBg: "#a7f3d0",
            hoverText: "#065f46",
          },
          outline: {
            bg: "rgba(16, 185, 129, 0.05)",
            text: "#10b981",
            border: "rgba(16, 185, 129, 0.3)",
            hoverBg: "#10b981",
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: "#10b981",
            border: "transparent",
            hoverBg: "rgba(16, 185, 129, 0.1)",
            hoverText: "#10b981",
          },
        };

      case "warning":
        return {
          solid: {
            bg: "#f59e0b",
            text: "#ffffff",
            border: "#f59e0b",
            hoverBg: "#d97706",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#fef3c7",
            text: "#92400e",
            border: "transparent",
            hoverBg: "#fde68a",
            hoverText: "#92400e",
          },
          outline: {
            bg: "rgba(245, 158, 11, 0.05)",
            text: "#f59e0b",
            border: "rgba(245, 158, 11, 0.3)",
            hoverBg: "#f59e0b",
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: "#f59e0b",
            border: "transparent",
            hoverBg: "rgba(245, 158, 11, 0.1)",
            hoverText: "#f59e0b",
          },
        };
    }
  };

  // Variant configurations
  const getVariantStyles = () => {
    const colorStyles = getColorStyles(color);

    switch (variant) {
      case "primary":
        return {
          backgroundColor: colorStyles.solid.bg,
          color: colorStyles.solid.text,
          borderColor: colorStyles.solid.border,
          hoverBg: colorStyles.solid.hoverBg,
          hoverColor: colorStyles.solid.hoverText,
        };

      case "secondary":
        return {
          backgroundColor: colorStyles.secondary.bg,
          color: colorStyles.secondary.text,
          borderColor: colorStyles.secondary.border,
          hoverBg: colorStyles.secondary.hoverBg,
          hoverColor: colorStyles.secondary.hoverText,
        };

      case "outline":
        return {
          backgroundColor: colorStyles.outline.bg,
          color: colorStyles.outline.text,
          borderColor: colorStyles.outline.border,
          hoverBg: colorStyles.outline.hoverBg,
          hoverColor: colorStyles.outline.hoverText,
        };

      case "ghost":
        return {
          backgroundColor: colorStyles.ghost.bg,
          color: colorStyles.ghost.text,
          borderColor: colorStyles.ghost.border,
          hoverBg: colorStyles.ghost.hoverBg,
          hoverColor: colorStyles.ghost.hoverText,
        };

      case "destructive":
        // Destructive variant always uses destructive color
        const destructiveStyles = getColorStyles("destructive");
        return {
          backgroundColor: destructiveStyles.solid.bg,
          color: destructiveStyles.solid.text,
          borderColor: destructiveStyles.solid.border,
          hoverBg: destructiveStyles.solid.hoverBg,
          hoverColor: destructiveStyles.solid.hoverText,
        };

      default:
        return {
          backgroundColor: colorStyles.solid.bg,
          color: colorStyles.solid.text,
          borderColor: colorStyles.solid.border,
          hoverBg: colorStyles.solid.hoverBg,
          hoverColor: colorStyles.solid.hoverText,
        };
    }
  };

  const styles = getVariantStyles();

  // Add 3D effects for primary and secondary variants
  const get3DEffects = (variant: string, color: string) => {
    if (variant === "primary" || variant === "secondary") {
      const isSecondary = variant === "secondary";

      // Adjust shadow intensity for secondary variant
      const shadowMultiplier = isSecondary ? 0.7 : 1;
      const highlightMultiplier = isSecondary ? 0.6 : 1;

      // Enhanced base shadow for better depth (no drop shadow for secondary)
      const baseShadow = isSecondary
        ? `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.25 * highlightMultiplier})`
        : `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.25 * highlightMultiplier})`;

      // Hover shadow for enhanced depth (no drop shadow for secondary)
      const hoverShadow = isSecondary
        ? `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.35 * highlightMultiplier})`
        : `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.35 * highlightMultiplier})`;

      // Active shadow for pressed state
      const activeShadow = `inset 0 3px 6px 0 rgba(0, 0, 0, ${0.15 * shadowMultiplier}), inset 0 1px 2px 0 rgba(255, 255, 255, ${0.2 * highlightMultiplier})`;

      return {
        baseShadow,
        hoverShadow,
        activeShadow,
        transform: "translateY(0)",
        hoverTransform: "translateY(0)",
        activeTransform: "translateY(1px)",
      };
    }

    return {
      baseShadow: "none",
      hoverShadow: "none",
      activeShadow: "none",
      transform: "translateY(0)",
      hoverTransform: "translateY(0)",
      activeTransform: "translateY(0)",
    };
  };

  const effects = get3DEffects(variant, color);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    e.currentTarget.style.backgroundColor = styles.hoverBg;
    if (styles.hoverColor) {
      e.currentTarget.style.color = styles.hoverColor;
    }
    // Apply hover effects for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.hoverShadow;
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    e.currentTarget.style.backgroundColor = styles.backgroundColor;
    e.currentTarget.style.color = styles.color;
    // Reset effects for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.baseShadow;
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    // Apply active effects for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.activeShadow;
      e.currentTarget.style.transform = effects.activeTransform;
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    // Reset to hover state for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.hoverShadow;
      e.currentTarget.style.transform = effects.transform;
    }
  };

  // Arrow icon component - chevron that morphs into arrow on hover
  const ArrowIcon = () => (
    <svg
      className={`${currentSize.iconSize} transition-transform duration-300 group-hover:translate-x-1`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Base chevron path */}
      <path
        className="transition-all duration-300 ease-out"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 6l7 6-7 6"
      />
      {/* Arrow line that appears on hover */}
      <path
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 12H4"
      />
    </svg>
  );

  // Play icon component
  const PlayIcon = () => (
    <svg className={currentSize.iconSize} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 5v14l11-7z" />
    </svg>
  );

  // Shared className + inline style, identical for the button and the link form so an `href`
  // FetchButton is pixel-for-pixel the same as a click one.
  const sharedClassName = `${baseClasses} ${className} group`;
  const sharedStyle: React.CSSProperties = {
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    borderColor: styles.borderColor,
    outline: "none",
    boxShadow: variant === "primary" || variant === "secondary" ? effects.baseShadow : "none",
    transform: variant === "primary" || variant === "secondary" ? effects.transform : "translateY(0)",
    transition:
      variant === "primary" || variant === "secondary" ? "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)" : "all 0.3s ease",
    position: "relative",
    ...(loading &&
      dimensions.width > 0 && {
        minWidth: `${dimensions.width}px`,
        minHeight: `${dimensions.height}px`,
      }),
  };

  // The icon/label content is identical for both element types.
  const content = (
    <>
      {/* Gradient overlay for primary and secondary variants */}
      {(variant === "primary" || variant === "secondary") && (
        <div
          className="absolute inset-0 rounded-xl opacity-20 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)`,
          }}
        />
      )}

      {loading ? (
        <Loader2 className={`${currentSize.iconSize} animate-spin`} />
      ) : iconOnly ? (
        <span className={`${currentSize.iconSize} flex-shrink-0 relative z-10 flex items-center justify-center`}>
          {leftIcon || rightIcon || children}
        </span>
      ) : (
        <>
          {leftIcon && (
            <span className={`${currentSize.iconSize} flex-shrink-0 relative z-10 flex items-center justify-center`}>
              {leftIcon}
            </span>
          )}

          {children && <span className="truncate min-w-0 relative z-10">{children}</span>}

          {arrow && <ArrowIcon />}

          {rightIcon && (
            <span className={`${currentSize.iconSize} flex-shrink-0 relative z-10 flex items-center justify-center`}>
              {rightIcon}
            </span>
          )}
        </>
      )}
    </>
  );

  // When `href` is set, render a real anchor (so it links / opens new tabs) with identical styling.
  // A disabled link has no meaning, so a disabled `href` button falls back to the <button> form.
  const button =
    href && !disabled && !ariaDisabled && !loading ? (
      <a
        href={href}
        target={target}
        rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
        className={sharedClassName}
        style={sharedStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
      >
        {content}
      </a>
    ) : (
      <button
        ref={buttonRef}
        // See `ariaDisabled`: Enter on a submit button bypasses `onClick` entirely.
        type={ariaDisabled ? "button" : type}
        className={sharedClassName}
        style={sharedStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        // `ariaDisabled` is advisory to the browser, so the click has to be swallowed here.
        onClick={ariaDisabled ? undefined : onClick}
        disabled={disabled || loading}
        aria-disabled={ariaDisabled || undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        aria-pressed={ariaPressed}
      >
        {content}
      </button>
    );

  if (tooltip) {
    return (
      <FetchTooltip content={tooltip} side={tooltipSide} align={tooltipAlign}>
        {button}
      </FetchTooltip>
    );
  }

  return button;
};
